import React, { useEffect, useState, useCallback } from 'react';
import { api, fmtMoney, formatApiError } from '../../lib/api';
import { useDarkMode } from '../../context/DarkModeContext';
import { Plus, Trash2, Copy, ExternalLink, Repeat, Tag, Zap } from 'lucide-react';

const INTERVAL_LABEL = { one_time: 'One-time', day: 'Daily', week: 'Weekly', month: 'Monthly', year: 'Yearly' };

const Products = ({ lob }) => {
  const darkMode = useDarkMode();
  const aBg = darkMode ? lob?.accentBgDark : lob?.accentBg;
  const aTc = darkMode ? lob?.accentTcDark : lob?.accentTc;
  const [products, setProducts] = useState([]);
  const [stripe, setStripe] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [linkFor, setLinkFor] = useState(null);
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    if (!lob) return;
    api.get(`/products?lob_id=${lob.id}`).then((r) => setProducts(r.data)).catch((e) => setErr(formatApiError(e)));
    api.get('/settings/integrations/stripe').then((r) => setStripe(r.data)).catch(() => {});
  }, [lob?.id]);
  useEffect(() => { load(); }, [load]);

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try { await api.delete(`/products/${p.id}`); load(); } catch (e) { alert(formatApiError(e)); }
  };

  return (
    <>
      <div className="page-toolbar" data-testid="products-toolbar">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span className="page-title">Products</span>
          <span className="lob-badge" style={{ background: aBg }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: lob.accent, display: 'inline-block', marginRight: 4 }} />
            <span style={{ color: aTc }}>{lob.label}</span>
          </span>
        </div>
        <button className="cta-btn" style={{ background: lob.accent }} onClick={() => setShowCreate(true)} data-testid="add-product-btn"><Plus size={11} style={{ marginRight: 4 }} />Add product</button>
      </div>

      {stripe?.mode === 'dev_proxy' && (
        <div className="module-banner" data-testid="stripe-dev-banner">
          ⚠️ <b>Dev Stripe key in use.</b> One-time checkouts work end-to-end. Subscriptions, refunds, and one-click upsells require your own Stripe key — paste <code>sk_test_…</code> or <code>sk_live_…</code> into <code>backend/.env</code> and restart to unlock the full commerce stack.
        </div>
      )}
      {err && <div className="module-banner err">{err}</div>}

      <div className="content-area">
        {products.length === 0 ? (
          <div className="empty-state" data-testid="products-empty">
            <Tag size={36} style={{ opacity: 0.25 }} />
            <div className="ttl">No products yet</div>
            <div className="sub">Create your first product to start selling.</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
            {products.map((p) => (
              <div key={p.id} className="feature-card" style={{ margin: 0 }} data-testid={`product-card-${p.id}`}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  {p.image_url && <img src={p.image_url} alt="" style={{ width: 48, height: 48, borderRadius: 6, objectFit: 'cover' }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="ttl" style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                      {p.interval !== 'one_time' ? <span className="tag-pill warn"><Repeat size={9} style={{ marginRight: 3 }} />{INTERVAL_LABEL[p.interval]}</span> : <span className="tag-pill muted">One-time</span>}
                    </div>
                    <div className="sub" style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{p.description || '—'}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 10 }}>
                  <span style={{ fontSize: 18, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>{fmtMoney(p.price)}</span>
                  <span style={{ fontSize: 10, color: 'var(--text-faded)', textTransform: 'uppercase' }}>{p.currency}</span>
                  {p.interval !== 'one_time' && <span style={{ fontSize: 10, color: 'var(--text-faded)' }}>/ {p.interval}</span>}
                </div>
                {p.upsell_product_id && (
                  <div style={{ fontSize: 10, color: 'var(--text-faded)', marginTop: 4 }}>
                    <Zap size={9} style={{ marginRight: 3 }} />Upsell configured{p.upsell_discount_pct ? ` (${p.upsell_discount_pct}% off)` : ''}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 4, marginTop: 10 }}>
                  <button className="dp-btn primary" onClick={() => setLinkFor(p)} disabled={p.interval !== 'one_time' && !stripe?.supports_subscriptions} title={p.interval !== 'one_time' && !stripe?.supports_subscriptions ? 'Subscription checkout requires real Stripe key' : ''} data-testid={`buy-link-${p.id}`}>Get buy link</button>
                  <button className="dp-btn" onClick={() => setEditing(p)} data-testid={`edit-prod-${p.id}`}>Edit</button>
                  <button className="dp-btn" style={{ color: 'var(--error)' }} onClick={() => del(p)} data-testid={`del-prod-${p.id}`}><Trash2 size={10} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {(showCreate || editing) && (
        <ProductModal lob={lob} product={editing} products={products} stripe={stripe} onClose={(saved) => { setShowCreate(false); setEditing(null); if (saved) load(); }} />
      )}
      {linkFor && <BuyLinkModal product={linkFor} onClose={() => setLinkFor(null)} />}
    </>
  );
};

const ProductModal = ({ lob, product, products, stripe, onClose }) => {
  const [form, setForm] = useState(product || {
    lob_id: lob.id, name: '', description: '', image_url: '',
    price: '', currency: 'usd', interval: 'one_time', interval_count: 1,
    upsell_product_id: '', upsell_discount_pct: '', is_active: true,
    bump_offers: [],
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Bump-offer helpers
  const bumps = form.bump_offers || [];
  const addBump = (kind) => set('bump_offers', [...bumps, kind === 'product'
    ? { id: crypto.randomUUID(), label: '', description: '', product_id: '', currency: form.currency || 'usd', default_checked: false }
    : { id: crypto.randomUUID(), label: '', description: '', price: '', currency: form.currency || 'usd', default_checked: false }
  ]);
  const updateBump = (idx, patch) => set('bump_offers', bumps.map((b, i) => i === idx ? { ...b, ...patch } : b));
  const removeBump = (idx) => set('bump_offers', bumps.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    const num = (n) => n === '' || n == null ? null : Number(n);
    const cleanBumps = bumps
      .filter((b) => (b.label || '').trim() && (b.product_id || (b.price !== '' && b.price != null)))
      .map((b) => ({
        ...b,
        product_id: b.product_id || null,
        price: b.product_id ? null : Number(b.price),
        currency: b.currency || 'usd',
      }));
    const payload = {
      ...form,
      price: Number(form.price),
      interval_count: Number(form.interval_count) || 1,
      upsell_product_id: form.upsell_product_id || null,
      upsell_discount_pct: num(form.upsell_discount_pct),
      bump_offers: cleanBumps,
    };
    try {
      if (product) await api.patch(`/products/${product.id}`, payload);
      else await api.post('/products', payload);
      onClose(true);
    } catch (ex) { setErr(formatApiError(ex)); } finally { setBusy(false); }
  };

  const subsDisabled = stripe && !stripe.supports_subscriptions;
  const oneTimeOthers = (products || []).filter((p) => p.interval === 'one_time' && p.id !== product?.id);

  return (
    <div className="modal-overlay-v2" onClick={() => onClose(false)}>
      <form className="modal-card wide" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="product-modal">
        <div className="modal-title">{product ? `Edit ${product.name}` : 'Add product'}</div>
        <div className="modal-row">
          <div><label className="f-label">Name</label><input className="f-input" required value={form.name} onChange={(e) => set('name', e.target.value)} data-testid="pm-name" /></div>
          <div><label className="f-label">Image URL (optional)</label><input className="f-input" value={form.image_url} onChange={(e) => set('image_url', e.target.value)} placeholder="https://…" /></div>
        </div>
        <label className="f-label">Description</label>
        <textarea className="f-textarea" rows={2} value={form.description} onChange={(e) => set('description', e.target.value)} data-testid="pm-description" />
        <div className="modal-row">
          <div><label className="f-label">Price</label><input className="f-input" type="number" step="0.01" min="0.50" required value={form.price} onChange={(e) => set('price', e.target.value)} data-testid="pm-price" /></div>
          <div><label className="f-label">Currency</label>
            <select className="f-select" value={form.currency} onChange={(e) => set('currency', e.target.value)}>
              <option value="usd">USD</option><option value="eur">EUR</option><option value="gbp">GBP</option><option value="cad">CAD</option>
            </select>
          </div>
          <div><label className="f-label">Billing</label>
            <select className="f-select" value={form.interval} onChange={(e) => set('interval', e.target.value)} data-testid="pm-interval">
              <option value="one_time">One-time</option>
              <option value="month" disabled={subsDisabled}>Monthly</option>
              <option value="year" disabled={subsDisabled}>Yearly</option>
              <option value="week" disabled={subsDisabled}>Weekly</option>
              <option value="day" disabled={subsDisabled}>Daily</option>
            </select>
            {subsDisabled && <div style={{ fontSize: 9, color: 'var(--text-faded)', marginTop: 2 }}>Subscriptions need real Stripe key</div>}
          </div>
        </div>
        <label className="f-checkbox"><input type="checkbox" checked={form.is_active} onChange={() => set('is_active', !form.is_active)} />Active (available for purchase)</label>

        <div style={{ fontSize: 12, fontWeight: 600, marginTop: 14, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
          <Zap size={11} /> One-click upsell <span className="tag-pill muted" style={{ fontSize: 8 }}>optional</span>
        </div>
        <div style={{ fontSize: 10, color: 'var(--text-faded)', marginBottom: 6 }}>After a buyer pays for this product, show them another one-time product to add with one click using their saved card.</div>
        <div className="modal-row">
          <div>
            <label className="f-label">Offer this upsell</label>
            <select className="f-select" value={form.upsell_product_id || ''} onChange={(e) => set('upsell_product_id', e.target.value)} disabled={!stripe?.supports_upsells} data-testid="pm-upsell-product">
              <option value="">— None —</option>
              {oneTimeOthers.map((p) => <option key={p.id} value={p.id}>{p.name} (${p.price})</option>)}
            </select>
            {!stripe?.supports_upsells && <div style={{ fontSize: 9, color: 'var(--text-faded)', marginTop: 2 }}>Needs real Stripe key</div>}
          </div>
          <div><label className="f-label">Discount % (optional)</label><input className="f-input" type="number" min="0" max="100" step="1" value={form.upsell_discount_pct} onChange={(e) => set('upsell_discount_pct', e.target.value)} placeholder="0" /></div>
        </div>

        {/* ── Order-bump add-ons ─────────────────────────────────────────── */}
        <div style={{ fontSize: 12, fontWeight: 600, marginTop: 18, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
          <Plus size={11} /> Order bumps <span className="tag-pill muted" style={{ fontSize: 8 }}>optional · checkbox add-ons</span>
        </div>
        <div style={{ fontSize: 10, color: 'var(--text-faded)', marginBottom: 6 }}>
          Show checkboxes on the buy page that let buyers add extras to the SAME checkout (e.g. "Add the Quick-Start Checklist for $29"). Each bump can either reuse an existing product OR be a free-form one-off.
        </div>
        {bumps.length === 0 && (
          <div style={{ fontSize: 10, color: 'var(--text-soft)', padding: 8, background: 'var(--bg-weak)', borderRadius: 4, marginBottom: 6 }}>
            No bumps yet. Add one below.
          </div>
        )}
        {bumps.map((b, i) => (
          <div key={b.id || i} style={{ border: '1px solid var(--stroke-lighter)', padding: 8, borderRadius: 4, marginBottom: 6, background: 'var(--bg-white)' }} data-testid={`bump-row-${i}`}>
            <div className="modal-row">
              <div style={{ gridColumn: 'span 2' }}>
                <label className="f-label">Bump label (shown to buyer)</label>
                <input className="f-input" value={b.label || ''} onChange={(e) => updateBump(i, { label: e.target.value })} placeholder="YES! Add the Quick-Start Checklist for just $29" data-testid={`bump-label-${i}`} />
              </div>
              <div>
                <label className="f-label">&nbsp;</label>
                <label className="f-checkbox" style={{ fontSize: 10 }}>
                  <input type="checkbox" checked={!!b.default_checked} onChange={() => updateBump(i, { default_checked: !b.default_checked })} />
                  Pre-checked
                </label>
              </div>
            </div>
            <label className="f-label">Short description (optional)</label>
            <input className="f-input" value={b.description || ''} onChange={(e) => updateBump(i, { description: e.target.value })} placeholder="Get started in under 10 minutes" />
            <div className="modal-row">
              <div>
                <label className="f-label">Link to an existing product (reusable, recommended)</label>
                <select className="f-select" value={b.product_id || ''} onChange={(e) => updateBump(i, { product_id: e.target.value || null, price: e.target.value ? null : (b.price || '') })} data-testid={`bump-product-${i}`}>
                  <option value="">— Free-form price below —</option>
                  {oneTimeOthers.map((p) => <option key={p.id} value={p.id}>{p.name} (${p.price})</option>)}
                </select>
              </div>
              <div>
                <label className="f-label">Free-form price (used only if no product picked)</label>
                <input className="f-input" type="number" step="0.01" min="0.50" value={b.product_id ? '' : (b.price ?? '')} onChange={(e) => updateBump(i, { price: e.target.value, product_id: null })} placeholder="29.00" disabled={!!b.product_id} />
              </div>
              <div>
                <label className="f-label">&nbsp;</label>
                <button type="button" className="dp-btn" style={{ color: 'var(--error)' }} onClick={() => removeBump(i)} data-testid={`bump-remove-${i}`}><Trash2 size={10} /> Remove</button>
              </div>
            </div>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" className="btn-secondary" onClick={() => addBump('product')} data-testid="bump-add-product"><Plus size={10} /> Add bump from product</button>
          <button type="button" className="btn-secondary" onClick={() => addBump('free')} data-testid="bump-add-freeform"><Plus size={10} /> Add free-form bump</button>
        </div>

        {err && <div className="f-error" data-testid="pm-error">{err}</div>}
        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={() => onClose(false)}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy} data-testid="pm-submit">{busy ? 'Saving…' : (product ? 'Save' : 'Create')}</button>
        </div>
      </form>
    </div>
  );
};

const BuyLinkModal = ({ product, onClose }) => {
  const [email, setEmail] = useState('');
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const publicUrl = `${window.location.origin}/buy/${product.slug || product.id}`;

  const gen = async () => {
    setBusy(true); setErr('');
    try {
      const { data } = await api.post(`/products/${product.id}/checkout-link`, {
        product_id: product.id,
        origin_url: window.location.origin,
        customer_email: email || null,
        quantity: 1,
      });
      setLink(data.url);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="modal-overlay-v2" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()} data-testid="buy-link-modal">
        <div className="modal-title">{product.name} — share to sell</div>
        <div className="feature-card" style={{ margin: 0, marginBottom: 10 }}>
          <div className="ttl" style={{ fontSize: 12 }}><ExternalLink size={11} /> Public product page</div>
          <div className="sub">Anyone with this URL can purchase. Lives forever.</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <input className="f-input" readOnly value={publicUrl} onFocus={(e) => e.target.select()} data-testid="buy-public-url" />
            <button className="btn-secondary" onClick={() => navigator.clipboard.writeText(publicUrl)} type="button">Copy</button>
            <a className="btn-primary" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }} href={publicUrl} target="_blank" rel="noreferrer">Open</a>
          </div>
        </div>
        <div className="feature-card" style={{ margin: 0 }}>
          <div className="ttl" style={{ fontSize: 12 }}>Or generate a one-shot Stripe Checkout link</div>
          <div className="sub">Pre-fills the customer email and skips the product page. Expires after a few hours.</div>
          <label className="f-label" style={{ marginTop: 10 }}>Customer email (optional)</label>
          <input className="f-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="buyer@example.com" data-testid="buy-link-email" />
          {!link ? (
            <button className="btn-primary" disabled={busy} onClick={gen} style={{ marginTop: 10 }} data-testid="generate-buy-link">{busy ? 'Generating…' : 'Generate checkout link'}</button>
          ) : (
            <div style={{ marginTop: 10 }}>
              <input className="f-input" readOnly value={link} onFocus={(e) => e.target.select()} data-testid="buy-link-url" />
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <button className="btn-secondary" onClick={() => navigator.clipboard.writeText(link)} type="button">Copy</button>
                <a className="btn-primary" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }} href={link} target="_blank" rel="noreferrer">Open Stripe Checkout</a>
              </div>
            </div>
          )}
          {err && <div className="f-error">{err}</div>}
        </div>
        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default Products;
